import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, Kicker, EASE } from "./motion";
import { ArrowRight, Check } from "lucide-react";

const imports = ["Products", "Recipes", "Stock", "Staff", "Historical sales"];

const rawRows = [
  "burgr_classic,8.5,mains,,",
  "fish&chips ,13,,gf?,",
  "HOUSE RED 175ml,6.5,drink,,",
  "brownie,6,pud,v,nut",
];

const tidyRows = [
  { name: "Classic Burger", price: "£8.50", cat: "Mains" },
  { name: "Fish & Chips", price: "£13.00", cat: "Mains", tag: "GF" },
  { name: "House Red, 175ml", price: "£6.50", cat: "Drinks" },
  { name: "Brownie", price: "£6.00", cat: "Puddings", tag: "V" },
];

function CsvToData() {
  return (
    <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-8 md:gap-6">
      <div className="rounded-xl border border-verlo/10 bg-paper p-6 shadow-soft">
        <p className="text-[11px] tracking-[0.2em] uppercase text-verlo/40 mb-4">Your export</p>
        <div className="space-y-2 font-mono text-xs md:text-sm text-verlo/50">
          {rawRows.map((r, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="truncate"
            >
              {r}
            </motion.p>
          ))}
        </div>
      </div>

      <div className="flex md:flex-col items-center justify-center gap-2 text-brass">
        <span className="text-[11px] tracking-[0.2em] uppercase">Bridge</span>
        <ArrowRight className="rotate-90 md:rotate-0" size={22} />
      </div>

      <div className="rounded-xl border border-verlo/10 bg-paper p-6 shadow-soft">
        <p className="text-[11px] tracking-[0.2em] uppercase text-verlo/40 mb-4">In Verlo</p>
        <div className="space-y-3">
          {tidyRows.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.5 + i * 0.12 }}
              className="flex items-center justify-between gap-3 border-b border-verlo/10 pb-2 last:border-0"
            >
              <span className="text-sm md:text-base text-verlo">{r.name}</span>
              <span className="flex items-center gap-2 shrink-0">
                {r.tag && (
                  <span className="text-[10px] tracking-wide uppercase text-brass border border-brass/30 rounded-full px-2 py-0.5">
                    {r.tag}
                  </span>
                )}
                <span className="text-[11px] uppercase tracking-wide text-verlo/40">{r.cat}</span>
                <span className="tnum text-sm text-verlo/80">{r.price}</span>
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Bridge() {
  const navigate = useNavigate();
  return (
    <section
      id="bridge"
      data-testid="chapter-bridge"
      className="px-6 md:px-12 py-28 md:py-44 border-t border-verlo/10"
    >
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <div className="flex items-baseline gap-4 mb-8">
            <span className="font-display text-brass text-lg">IV</span>
            <Kicker>Bridge</Kicker>
          </div>
          <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.1] text-verlo max-w-3xl">
            Moving EPOS? Don&apos;t. Move everything else.
          </h2>
        </Reveal>

        <Reveal>
          <p className="mt-10 text-xl md:text-2xl leading-loose text-verlo max-w-xl">
            Changing tills used to mean weeks of re-keying and a nervous first Saturday.
            Bridge does it quietly. Hand it your old data and it brings the lot across.
          </p>
        </Reveal>

        {/* import checklist */}
        <div className="mt-16 md:mt-20 grid md:grid-cols-[auto_1fr] gap-10 md:gap-20 items-center">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-verlo/60">
              <span className="font-display text-lg md:text-xl line-through decoration-verlo/30">
                Old system
              </span>
              <ArrowRight size={18} className="text-brass" />
              <span className="font-display text-lg md:text-xl text-brass">Bridge</span>
              <ArrowRight size={18} className="text-brass" />
              <span className="font-display text-lg md:text-xl text-verlo">Verlo, ready</span>
            </div>
          </Reveal>

          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {imports.map((item, i) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.12 }}
                className="flex items-center gap-2 text-sm md:text-base text-verlo border-b border-verlo/15 pb-3"
              >
                <Check size={16} className="text-brass shrink-0" />
                {item}
              </motion.li>
            ))}
          </ul>
        </div>

        <Reveal>
          <div className="mt-16 md:mt-24">
            <CsvToData />
          </div>
        </Reveal>

        <Reveal>
          <div
            data-testid="bridge-sandbox-callout"
            className="mt-20 md:mt-28 border-t border-verlo/15 pt-10"
          >
            <p className="font-display font-light text-2xl md:text-4xl leading-snug text-verlo max-w-3xl">
              See your restaurant running inside Verlo before you speak to sales.{" "}
              <button
                data-testid="bridge-try-verlo-link"
                onClick={() => navigate("/sandbox")}
                className="text-brass underline underline-offset-4 decoration-brass/40 hover:decoration-brass transition-all duration-300"
              >
                Try Verlo
              </button>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
